import { useState } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import {
  AlertTriangle, ArrowUpLeft, Boxes, CheckCircle2, PackageX, ShieldCheck, TrendingUp,
} from 'lucide-react'
import { formatMoney, formatNumber, PERMISSIONS, type ProductListItem } from '@najah/core'
import {
  Alert, Badge, Button, Card, CardBody, CardHeader, EmptyState, Spinner,
  Table, TableSkeleton, TableWrapper, Tbody, Td, Th, Thead, Tr, cn,
} from '@najah/ui'
import { inventoryApi, productsApi } from '../lib/catalogApi'
import { useAuth } from '../lib/auth'
import { StockAdjustDialog } from '../components/StockAdjustDialog'

function StatCard({
  label, value, icon: Icon, tone, hint,
}: { label: string; value: string; icon: typeof Boxes; tone: 'primary' | 'accent' | 'warning' | 'danger'; hint?: string }) {
  const tones = {
    primary: 'bg-primary-subtle text-primary-subtle-fg',
    accent: 'bg-accent-subtle text-accent-subtle-fg',
    warning: 'bg-warning-subtle text-warning',
    danger: 'bg-danger-subtle text-danger',
  } as const

  return (
    <Card>
      <CardBody className="flex items-start gap-4">
        <span className={cn('grid size-11 shrink-0 place-items-center rounded-lg', tones[tone])}>
          <Icon className="size-5" />
        </span>
        <div className="min-w-0">
          <p className="text-sm text-fg-muted">{label}</p>
          <p className="mt-0.5 text-2xl font-bold tracking-tight text-fg" dir="ltr">{value}</p>
          {hint && <p className="mt-1 text-xs text-fg-subtle">{hint}</p>}
        </div>
      </CardBody>
    </Card>
  )
}

function AttentionTable({
  title, description, filter, emptyText, tone,
}: {
  title: string
  description: string
  filter: 'low' | 'out'
  emptyText: string
  tone: 'warning' | 'danger'
}) {
  const { can } = useAuth()
  const [adjusting, setAdjusting] = useState<ProductListItem | null>(null)

  const { data, isPending } = useQuery({
    queryKey: ['products', { stock: filter, pageSize: 50 }],
    queryFn: () => productsApi.list({ stock: filter, pageSize: 50, page: 1 }),
    placeholderData: keepPreviousData,
  })

  return (
    <Card>
      <CardHeader
        title={title}
        description={description}
        action={data ? <Badge tone={data.items.length ? tone : 'success'}>{formatNumber(data.items.length)}</Badge> : null}
      />

      {isPending ? (
        <TableWrapper className="rounded-none border-0">
          <Table><TableSkeleton rows={3} cols={3} /></Table>
        </TableWrapper>
      ) : data && data.items.length > 0 ? (
        <TableWrapper className="rounded-none border-0 border-t">
          <Table>
            <Thead>
              <tr>
                <Th>المنتج</Th>
                <Th align="center">الرصيد</Th>
                <Th align="center">الحدّ</Th>
                {can(PERMISSIONS.STOCK_ADJUST) && <Th align="center">إجراء</Th>}
              </tr>
            </Thead>
            <Tbody>
              {data.items.map((p) => (
                <Tr key={p.id}>
                  <Td>
                    <p className="truncate font-medium text-fg">{p.nameAr}</p>
                    <p className="font-mono text-xs text-fg-subtle" dir="ltr">{p.sku}</p>
                  </Td>
                  <Td align="center">
                    <span
                      className={cn('font-semibold tabular-nums', p.quantity <= 0 ? 'text-danger' : 'text-warning')}
                      dir="ltr"
                    >
                      {formatNumber(p.quantity)}
                    </span>
                    <span className="ms-1 text-xs text-fg-subtle">{p.unit}</span>
                  </Td>
                  <Td align="center" className="tabular-nums text-fg-muted" dir="ltr">{formatNumber(p.minStock)}</Td>
                  {can(PERMISSIONS.STOCK_ADJUST) && (
                    <Td align="center">
                      <Button variant="secondary" size="sm" onClick={() => setAdjusting(p)}>تسوية</Button>
                    </Td>
                  )}
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableWrapper>
      ) : (
        <EmptyState icon={<CheckCircle2 className="size-8 text-success" />} title={emptyText} />
      )}

      <StockAdjustDialog open={!!adjusting} onClose={() => setAdjusting(null)} product={adjusting} />
    </Card>
  )
}

export function InventoryPage() {
  const { data: summary, isPending } = useQuery({ queryKey: ['inventory', 'summary'], queryFn: inventoryApi.summary })
  const [checkIntegrity, setCheckIntegrity] = useState(false)
  const { data: integrity, isFetching: integrityLoading } = useQuery({
    queryKey: ['inventory', 'integrity'],
    queryFn: inventoryApi.integrity,
    enabled: checkIntegrity,
  })

  return (
    <div className="mx-auto max-w-7xl space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-fg">المخزون</h1>
          <p className="mt-0.5 text-sm text-fg-muted">نظرة عامة على الأرصدة والتنبيهات</p>
        </div>
        <Link
          to="/inventory/movements"
          className="inline-flex h-10 items-center gap-2 rounded-lg border border-border bg-surface px-4 text-sm font-medium text-fg transition-colors hover:bg-surface-hover"
        >
          سجل الحركات
          <ArrowUpLeft className="size-4" />
        </Link>
      </div>

      {isPending ? (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {Array.from({ length: 4 }, (_, i) => (
            <Card key={i}><CardBody><span className="block h-12 animate-pulse rounded bg-bg-subtle" /></CardBody></Card>
          ))}
        </div>
      ) : summary ? (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard
            label="قيمة المخزون" value={formatMoney(summary.stockValue)} icon={TrendingUp} tone="primary"
            hint="بسعر التكلفة"
          />
          <StatCard
            label="إجمالي الوحدات" value={formatNumber(summary.totalUnits)} icon={Boxes} tone="accent"
            hint={`عبر ${formatNumber(summary.totalProducts)} منتجاً`}
          />
          <StatCard
            label="على وشك النفاد" value={formatNumber(summary.lowStock)} icon={AlertTriangle} tone="warning"
            hint="بلغت حدّ التنبيه"
          />
          <StatCard
            label="نفدت" value={formatNumber(summary.outOfStock)} icon={PackageX} tone="danger"
            hint="رصيدها صفر"
          />
        </div>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-2">
        <AttentionTable
          title="على وشك النفاد"
          description="بلغ رصيدها حدّ التنبيه — أعد الطلب قريباً"
          filter="low"
          emptyText="لا توجد منتجات على وشك النفاد"
          tone="warning"
        />
        <AttentionTable
          title="نفدت من المخزون"
          description="رصيدها صفر — غير متاحة للبيع"
          filter="out"
          emptyText="كل المنتجات متوفّرة"
          tone="danger"
        />
      </div>

      {/* ── فحص سلامة السجل ── */}
      <Card>
        <CardHeader
          title="فحص سلامة المخزون"
          description="يقارن كل رصيد محسوب بمجموع سجل حركاته — يجب أن يتطابقا دائماً"
          action={
            <Button
              variant="secondary" size="sm"
              onClick={() => setCheckIntegrity(true)}
              loading={integrityLoading}
              startIcon={<ShieldCheck className="size-4" />}
            >
              تشغيل الفحص
            </Button>
          }
        />
        {checkIntegrity && (
          <CardBody>
            {integrityLoading ? (
              <div className="flex items-center gap-2 text-sm text-fg-muted">
                <Spinner className="size-4" /> جارٍ الفحص…
              </div>
            ) : integrity ? (
              integrity.healthy ? (
                <Alert tone="success" title="السجل سليم">
                  فُحص <span dir="ltr">{formatNumber(integrity.checked)}</span> منتجاً — كل رصيد يطابق مجموع حركاته
                  تماماً.
                </Alert>
              ) : (
                <Alert tone="danger" title={`انحراف في ${integrity.drift.length} منتجاً`}>
                  الرصيد المحسوب لا يطابق السجل. هذا يعني خللاً يجب فحصه — السجل هو المرجع الصحيح دائماً.
                </Alert>
              )
            ) : null}
          </CardBody>
        )}
      </Card>
    </div>
  )
}
